import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import html2pdf from 'html2pdf.js';

import BarChart from '../../charts/BarChart';
import RadarChart from '../../charts/RadarChart';
import PercentageChart from '../../charts/PercentageChart';
import home from '../../img/svg/home.svg';

const ResultsDisplay = ({ userData, categoryData }) => {

  const reportRef = useRef(null);

  const handleDownloadPDF = () => {
    const element = reportRef.current;
    const userName = userData?.info?.userName || 'Usuario';
    const diagnosticType = userData?.info?.diagnostic_type || 'Diagnostico';

    element.classList.add('pdf-mode');

    const opt = {
      margin:       [12, 12, 12, 12],
      filename:     `Resultados_${diagnosticType}_${userName}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, letterRendering: true, scrollY: 0 },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak:    { mode: ['css', 'legacy'], avoid: ['.chart-card', '.pdf-category-page'] }
    };

    html2pdf()
      .set(opt)
      .from(element)
      .save()
      .then(() => {
        element.classList.remove('pdf-mode');
      });
  };

  return (
    <div className="mx-auto max-w-[1200px] p-[50px] bg-[#f8f9fa] text-[#333] max-[850px]:p-5" style={{ fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>

      {/* BOTONERA */}
      <div data-html2canvas-ignore="true" className="mb-5 flex justify-between items-center">
        <Link to={'/'} className="flex items-center font-semibold no-underline gap-[5px] text-[#0056b3]">
          <img src={home} alt="Inicio" /> Volver al Inicio
        </Link>
        <button onClick={handleDownloadPDF} className="flex items-center text-white font-bold cursor-pointer border-0 bg-[#dc3545] py-[10px] px-5 rounded-[5px] text-base gap-2 transition-colors duration-300 hover:bg-[#c82333]">
          📥 Descargar PDF
        </button>
      </div>

      {/* ÁREA IMPRIMIBLE */}
      <div ref={reportRef} id="printable-area">

        {/* report-header: KEPT — targeted by #printable-area.pdf-mode .report-header in index.css */}
        <header className="report-header bg-white p-[40px] rounded-[8px] shadow-[0_2px_15px_rgba(0,0,0,0.05)] mb-[50px] border-l-[5px] border-[#0056b3]">
          <div>
            <h1 className="m-0 mb-[10px] text-[2.2rem] text-[#212529]">Resultados de tu Autodiagnóstico ICETEX</h1>
            <h2 className="user-subtitle m-0 text-[1.2rem] text-[#495057] font-normal">
              {userData?.info?.userName || 'Estudiante'} | {userData?.info?.diagnostic_type || 'Diagnóstico'}
            </h2>
            <p className="mt-[15px] text-[0.85rem] text-[#868e96] uppercase tracking-[0.5px]">Fecha: {new Date().toLocaleDateString()}</p>
          </div>
        </header>

        {/* executive-summary: KEPT — targeted by #printable-area.pdf-mode .executive-summary */}
        <section className="executive-summary">
          {/* section-title: KEPT — targeted by .detailed-results > .section-title in index.css */}
          <h3 className="section-title text-[1.6rem] text-[#0056b3] mb-[30px] pb-[15px] border-b-2 border-[#e9ecef] uppercase tracking-[1px]">1. Visión General de tus Resultados</h3>
          {/* charts-grid-two: KEPT — targeted by pdf-mode overrides */}
          <div className="charts-grid-two grid grid-cols-2 gap-[30px] mb-[60px] max-[850px]:grid-cols-1">
            {/* chart-card: KEPT — used in html2pdf pagebreak avoid */}
            <div className="chart-card bg-white text-center overflow-hidden py-[25px] px-[40px] pb-[5px] rounded-[8px] shadow-[0_4px_6px_rgba(0,0,0,0.04)] border border-[#e9ecef] [page-break-inside:avoid] [break-inside:avoid]">
              <h4 className="mt-0 text-[#495057] mb-5 font-semibold">Comparativo por Áreas</h4>
              {/* chart-wrapper: KEPT — targeted by pdf-mode .chart-wrapper */}
              <div className="chart-wrapper w-full relative min-h-[260px] [&:first-of-type]:-ml-[35px] [&:first-of-type]:mt-[35px]" style={{ marginLeft: '-35px', marginTop: '35px' }}>
                <BarChart categories={categoryData} />
              </div>
            </div>
            <div className="chart-card bg-white text-center overflow-hidden py-[25px] px-[40px] pb-[5px] rounded-[8px] shadow-[0_4px_6px_rgba(0,0,0,0.04)] border border-[#e9ecef] [page-break-inside:avoid] [break-inside:avoid]">
              <h4 className="mt-0 text-[#495057] mb-5 font-semibold">Perfil de Competencias</h4>
              <div className="chart-wrapper w-full relative min-h-[260px]" style={{ marginLeft: '-35px', marginTop: '20px' }}>
                <RadarChart categories={categoryData} />
              </div>
            </div>
          </div>
        </section>

        {/* detailed-results: KEPT — targeted by pdf-mode .detailed-results > .section-title */}
        <section className="detailed-results">
          <h3 className="section-title text-[1.6rem] text-[#0056b3] mb-[30px] pb-[15px] border-b-2 border-[#e9ecef] uppercase tracking-[1px]">2. Análisis Detallado y Recomendaciones</h3>
          {/* results-list: KEPT — targeted by pdf-mode .results-list */}
          <div className="results-list flex flex-col gap-[30px]">
            {categoryData.map((item, index) => {

              const categoryName = item.category?.name || item.name || `Área ${index + 1}`;

              const descriptionText =
                item.plan ||
                item.description ||
                "Continúa desarrollando tus habilidades en esta área. Consulta los recursos disponibles en la plataforma ICETEX.";

              return (
                /* pdf-category-page: KEPT — targeted by pdf-mode .pdf-category-page */
                <div className="pdf-category-page" key={index}>
                  {/* result-row: KEPT — targeted by pdf-mode .pdf-category-page .result-row */}
                  <article className="result-row flex bg-white overflow-hidden rounded-[8px] shadow-[0_4px_12px_rgba(0,0,0,0.06)] border border-[#e9ecef] max-[850px]:flex-col">
                    {/* result-chart: KEPT — targeted by pdf-mode and .result-chart selectors in index.css */}
                    <div className="result-chart flex items-center justify-center relative bg-[#e2e6ea] py-5 px-[15px] border-r border-[#e9ecef] max-[850px]:border-r-0 max-[850px]:border-b max-[850px]:py-5 max-[850px]:px-0" style={{ flex: '0 0 320px' }}>
                      <PercentageChart category={item} />
                    </div>
                    {/* result-text: KEPT — targeted by pdf-mode .pdf-category-page .result-text */}
                    <div className="result-text flex-1 flex flex-col p-[35px] justify-start max-[850px]:p-[25px]">
                      {/* dimension-name: KEPT — targeted by pdf-mode .dimension-name */}
                      <h4 className="dimension-name m-0 mb-[15px] text-[#0056b3] text-[1.4rem] font-bold">{categoryName}</h4>
                      {/* action-plan-body: KEPT — targeted by pdf-mode .action-plan-body */}
                      <div className="action-plan-body [&_p]:leading-[1.7] [&_p]:text-[#343a40] [&_p]:m-0 [&_p]:text-base [&_p]:text-justify" dangerouslySetInnerHTML={{ __html: descriptionText }} />
                    </div>
                  </article>
                </div>
              );
            })}
          </div>
        </section>

      </div>

      <footer data-html2canvas-ignore="true" className="text-center mt-[80px] pt-5 border-t border-[#dee2e6]">
        <p className="text-[#6c757d] text-base mb-5 leading-[1.6]">
          ¿Quieres mejorar en estas áreas? Explora los recursos educativos y programas de apoyo disponibles en ICETEX.
        </p>
        <Link
          className="inline-block text-white font-bold uppercase no-underline py-[14px] px-[40px] rounded-[50px] tracking-[0.5px] text-[0.9rem] shadow-[0_4px_15px_rgba(0,63,129,0.2)] transition-all duration-300 hover:-translate-y-[2px] hover:shadow-[0_6px_20px_rgba(0,63,129,0.3)]"
          style={{ background: 'linear-gradient(135deg, var(--color-azul-icetex) 0%, #0056b3 100%)' }}
          to='/diagnosticos'
        >
          Realizar nuevo diagnóstico
        </Link>
      </footer>
    </div>
  );
};

export default ResultsDisplay;
